import {
    forwardRef, memo, useCallback, useEffect,
    useImperativeHandle, useMemo, useReducer, useRef, useState
} from "react"

import { TableSurface } from "./components/TableSurface"
import { SeatsLayer } from "./components/seatsAndPlayerAndCards/SeatsLayer"
import { DealerIcon } from "./components/DealerIcon"
import { CenterCards } from "./components/CenterCards"
import { Layer, Stage } from "react-konva"
import { dealTrucoCards } from "./utils/dealTrucoCards"
import PlayerActions from "./components/PlayerActions"
import useSafeTimeout from "../../../../../Utillitys/useSafeTimeout"
import HandWinnerIndicator from "./components/pointIndicators/HandWinnerIndicator"
import TrucoInfoMessage from "./components/TrucoInfoMessage"
import TableHeader from "./components/TableHeader"

import gsap from "gsap"
import { Howl, Howler } from "howler"

import truco_theme from "../../../../../../../assets/sounds/truco_theme.mp3"
import { CARD_BACK_NOTATION, PHASE } from "./utils/TrucoGameUtils"
import { portraitTrucoLayout } from "./utils/portraitPositions"
import { landScapeTrucoLayout } from "./utils/positions"
import { discardTrucoCard } from "./utils/discardTrucoCard"
import { validateAction } from "../../TrucoGameEngine/trucoActionValidator"
import { useTrucoTableLayout } from "./hooks/useTrucoTableLayout"
import TrucoTimer from "./components/timers/TrucoTimer"
import { trucoGameReducer, initialTrucoGameState } from "./TrucoReducer/TrucoGameTableReducer"
import { forceCorrectRotation, mergeServerWithSeats, rotateSeats, verifyRotation } from "./utils/mergeAndRotateSeats"
import PointsAndHandWinIndicator from "./components/pointIndicators/PointsAndHandWinIndicator"
import { animateTrickResult } from "./utils/animateTrickResult"
import EnvidoPopUpMessage from "./gamePopUps/EnvidoPopUpMessage"
import TrucoGameWinPopUp from "./gamePopUps/TrucoGameWinPopUp"
import PointsAndScoring from "./components/pointIndicators/PointsAndScoring"
import CheckAndCrossMarksForCenterCards from "./components/CheckAndCrossMarksForCenterCards"
import TrucoDigitalTimer from "./components/timers/TrucoDigitalTimer"
import TableAndMatchInfo from "./components/TableAndMatchInfo"
import AlignGrid from "../../../../../../../library/alignGrid"
import SettingsDialog, { applyTheme } from "./gamePopUps/SettingsDialog"
import GameToastNotification from "./gamePopUps/GameToastNotification"

function debounce(func, delay) {
    let timeoutId
    return (...args) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => func(...args), delay)
    }
}

export const TableCanvas = memo(forwardRef((props, ref) => {
    const [gameState, dispatch] = useReducer(trucoGameReducer, initialTrucoGameState)

    // ── Destructure only what the render path needs ────────────────────────
    const {
        myPlayerId,
        table,
        phase: gamePhase,
        trickCount,
        dealerIndex,
        handIndex,
        gameDirection,
        seats,
        playerDetails,
        points,
        centerCards,
        playerActions,
        timer,
        isCenterCardsStack,
        action,
        envidoDetails,
        showEnvidoDetails,
        showGameWinDetails,
        gameWinnerDetails,
        symmetricOpponents,
        turn,
        bet,
        showRestartGameOptions
    } = gameState

    const { isSettingsOpen, notifPosition, activeTheme } = gameState.settings

    // ── Stable refs so callbacks never go stale ────────────────────────────
    // These are updated by lightweight effects and read inside useCallback/
    // imperative handles, so we never need those values in the dependency array.
    const gameStateRef = useRef(gameState)
    const seatsRef = useRef(seats)
    const tableRef = useRef(table)

    useEffect(() => { gameStateRef.current = gameState }, [gameState])
    useEffect(() => { seatsRef.current = seats }, [seats])
    useEffect(() => { tableRef.current = table }, [table])

    const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth)
    const [isCardsDealing, setIsCardsDealing] = useState(true)
    const [showPopUpMessage, setShowPopUpMessage] = useState(false)
    const [isThemeMuted, setIsThemeMute] = useState(false)
    const [playerActionMessage, setPlayerActionMessage] = useState("")

    const soundsRef = useRef({})
    const themeMusicRef = useRef(null)
    const isThemePlaying = useRef(false)
    const containerRef = useRef([])
    const seatRefs = useRef([])
    const dealerRef = useRef(null)
    // Holds every GSAP timeline that is currently animating cards.
    // Snap-to-end is called on each when the tab becomes visible again.
    const activeTimelinesRef = useRef([])

    const layout = isPortrait ? portraitTrucoLayout : landScapeTrucoLayout
    const {
        getSeatPosition, getCardPosition, getCenterCardPosition,
        getDealerIconPosition, getTimerPosition, getDigitalTimerPosition, getStackCenterCardPosition
    } = useTrucoTableLayout({ trickCount, totalSeats: seats.length, layout })

    const { setSafeTimeout, cancelTimeout } = useSafeTimeout()

    // ── Mount / teardown ───────────────────────────────────────────────────
    useEffect(() => {
        dispatch({
            type: "SET_TABLE",
            payload: { tableId: JSON.parse(localStorage.getItem("tableId")) }
        })
        dispatch({
            type: "SET_PLAYER_NAME",
            payload: sessionStorage.getItem("playerId")
        })
        if (!isPortrait) {
            dispatch({ type: "SET_SYMMETRIC_OPPONENTS", payload: true })
        }
        // if (!soundsRef.current?.cardFallSound) {
        //     soundsRef.current = {}
        //     themeMusicRef.current = new Howl({
        //         src: [truco_theme],
        //         html5: true,
        //         loop: true,
        //         volume: 1
        //     })
        //     playThemeMusic()
        // }

        // const resumeAudio = () => {
        //     const ctx = Howler.ctx
        //     if (!ctx) return
        //     if (!themeMusicRef.current) return
        //     if (ctx.state === "interrupted" || ctx.state === "suspended") {
        //         ctx.resume().then(() => {
        //             themeMusicRef?.current?.stop()
        //             themeMusicRef?.current?.play()
        //         })
        //     }
        // }

        // const suspendAudio = () => {
        //     const ctx = Howler.ctx
        //     if (!ctx) return
        //     const sound = themeMusicRef.current
        //     if (!sound) return
        //     if (ctx.state === "running") {
        //         ctx.suspend().then(() => { sound.stop() })
        //     } else if (ctx.state === "interrupted") {
        //         sound.stop()
        //     }
        // }

        // const onVisibilityChange = () => {
        //     if (document.hidden) {
        //         suspendAudio()
        //     } else {
        //         resumeAudio()
        //         // Tab became visible - snap any in-progress card animations
        //         // so the user never sees a mid-deal or mid-discard state.
        //         activeTimelinesRef.current.forEach(tl => {
        //             if (tl.snapAll) tl.snapAll()      // deal animation
        //             else if (tl.snapToEnd) tl.snapToEnd() // discard animation
        //         })
        //         activeTimelinesRef.current = []
        //     }
        // }

        const handleResize = debounce(() => {
            setIsPortrait(window.innerHeight > window.innerWidth)
        }, 200)

        handleResize()
        // window.addEventListener("visibilitychange", onVisibilityChange)
        // window.addEventListener("pagehide", suspendAudio)
        // window.addEventListener("pageshow", resumeAudio)
        window.addEventListener("resize", handleResize)
        // props.sendOf({ type: "START_MATCH" })

        return () => {
            window.removeEventListener("resize", handleResize)
            // window.removeEventListener("visibilitychange", onVisibilityChange)
            // window.removeEventListener("pagehide", suspendAudio)
            // window.removeEventListener("pageshow", resumeAudio)
            themeMusicRef.current?.stop()
            themeMusicRef.current = null
            Howler.unload()
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Seat initialisation ────────────────────────────────────────────────
    // Create blank placeholder seats - no isMe assumption here because we
    // don't yet know which server seat number belongs to the local player.
    // setMatchStart → mergeServerWithSeats is the authoritative source of
    // isMe, so we leave it false until then.
    useEffect(() => {
        if (!props.table) return
        // const numberOfSeats = 6
        const numberOfSeats = props.table.numberOfSeats
        const playerS = Array.from({ length: numberOfSeats }, (_, i) => ({
            id: i,
            name: i === Math.floor(numberOfSeats / 2) ? sessionStorage.getItem("playerId") : "",
            cards: [],
            points: 0,
            isMe: false,
            serverSeatNumber: null
        }))
        dispatch({ type: "SET_SEATS", payload: playerS })
    }, [props.table]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Card dealing "turn" message ────────────────────────────────────────
    // NOTE: we intentionally do NOT set a message here.
    // setPlayerActionButtons (called by the server right after the deal) is the
    // sole authority on whose turn it is.  Any message set here would either
    // race with setPlayerActionButtons or show a stale serverCanDraw value.

    // ── Theme mute ─────────────────────────────────────────────────────────
    useEffect(() => {
        themeMusicRef.current?.mute(isThemeMuted)
    }, [isThemeMuted])

    // ── Phase: reset TRICK_RESULT after one frame ──────────────────────────
    useEffect(() => {
        if (gamePhase !== PHASE.TRICK_RESULT) return
        const id = setSafeTimeout(() => {
            dispatch({ type: "SET_PHASE", payload: PHASE.PLAYING })
        }, 0)
        return () => cancelTimeout(id)
    }, [gamePhase]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Timer tick (wall-clock aligned) ───────────────────────────────────
    useEffect(() => {
        if (!timer.active) return

        const interval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - timer.startedAt) / 1000)
            const remaining = timer.duration - elapsed

            if (remaining <= 0) {
                dispatch({ type: "TICK_TIMER", payload: 0 })
                dispatch({ type: "STOP_TIMER" })
                clearInterval(interval)
                return
            }
            dispatch({ type: "TICK_TIMER", payload: remaining })
        }, 250)

        return () => clearInterval(interval)
    }, [timer.active, timer.startedAt, timer.duration])

    // ── Helpers ────────────────────────────────────────────────────────────
    const playThemeMusic = () => {
        if (props.isIphone) return
        if (themeMusicRef.current && !isThemePlaying.current) {
            themeMusicRef.current?.play()
            isThemePlaying.current = true
        }
    }

    // ── Handlers (stable callbacks) ────────────────────────────────────────

    const handleDealTrucoCards = useCallback((data, done) => {
        const gs = gameStateRef.current
        const currentSeats = seatsRef.current

        if (gs.phase !== PHASE.MATCH_STARTING) { done?.(); return }

        dispatch({ type: "SET_PHASE", payload: PHASE.DEALING })
        // Always mark dealing in progress up-front so isCardsDealing is true
        // before the first animation frame fires, regardless of prior state.
        setIsCardsDealing(true)

        const onDealingAnimationCompleted = () => {
            setIsCardsDealing(false)
            // ── Do NOT set a turn message here ────────────────────────────
            // The server always sends setPlayerActionButtons immediately after
            // handleDealTrucoCards completes. That handler calls START_CARD_PLAY
            // (setting serverCanDraw) and then setPlayerActionButtons sets the
            // message via the imperative handle below - so it is always the
            // canonical source of truth for whose turn it is after the deal.
            // Setting a message here would either race or show a stale value.
            setPlayerActionMessage("Waiting for your opponent to play.")
            dispatch({ type: "SET_PHASE", payload: PHASE.PLAYING })
            done()
        }

        const { tableId, roundId, matchId } = data
        dispatch({ type: "SET_TABLE", payload: { tableId, roundId, matchId } })

        const noCards = [CARD_BACK_NOTATION, CARD_BACK_NOTATION, CARD_BACK_NOTATION]
        let duplicateCards = [...currentSeats].map(ele => ({
            ...ele,
            cards: ele.playerId === data.playerId
                ? [...data.cards.map(c => `${c.value}${c.suit}`)]
                : [...noCards]
        }))
        // ── Wait for seatRefs to be populated ────────────────────────────
        // After RESET_GAME, seats briefly becomes [] then is repopulated by
        // setMatchStart. React unmounts/remounts the Seat elements in the next
        // render cycle, so seatRefs.current entries may still be null when
        // handleDealTrucoCards fires synchronously. Poll with rAF until all
        // refs are non-null before starting the deal animation.
        const startDeal = () => {
            const totalSeats = currentSeats.length
            const allRefsReady = totalSeats > 0 &&
                seatRefs.current.slice(0, totalSeats).every(r => r != null)
            if (!allRefsReady) {
                requestAnimationFrame(startDeal)
                return
            }
            const dealTl = dealTrucoCards({
                seats: duplicateCards,
                dealerIndex: gs.dealerIndex,
                seatRefs,
                dealerRef,
                cardsPerPlayer: 3,
                dispatch,
                onDealingAnimationCompleted,
                gameDirection: gs.gameDirection,
                getDealerIconPosition,
                getCardPosition
            })
            if (dealTl) activeTimelinesRef.current.push(dealTl)
        }
        // ── Rotation guard ─────────────────────────────────────────────────
        // Verify the local player is at the correct display index before
        // starting the deal.  On first game (or after a fast RESET_GAME cycle)
        // there is a narrow race where seatsRef has been repopulated but
        // rotateSeats() ran with an incorrect isMe assignment from
        // mergeServerWithSeats.  Catching it here — after seatRefs are all
        // mounted — means the animation always starts from a correct layout.

        const guardedSeats = verifyRotation(currentSeats)
            ? currentSeats
            : forceCorrectRotation(currentSeats)
        if (guardedSeats !== currentSeats) {
            // Rotation was wrong — correct it in the reducer and in local ref
            // so dealTrucoCards uses the right seat order.
            console.warn("[handleDealTrucoCards] Seat rotation was incorrect — correcting before deal.")
            dispatch({ type: "SET_SEATS", payload: guardedSeats })
            seatsRef.current = guardedSeats
            // Re-derive duplicateCards with the corrected seat order
            duplicateCards.splice(0, duplicateCards.length,
                ...guardedSeats.map(ele => ({
                    ...ele,
                    cards: ele.playerId === data.playerId
                        ? [...data.cards.map(c => `${c.value}${c.suit}`)]
                        : [...noCards]
                }))
            )
        }
        startDeal()
    }, [getDealerIconPosition, getCardPosition])

    const setMatchStart = useCallback((data, done) => {
        const gs = gameStateRef.current

        if (gs.phase !== PHASE.IDLE && gs.phase !== PHASE.MATCH_RESULT) {
            done?.(); return
        }

        dispatch({ type: "SET_PHASE", payload: PHASE.MATCH_STARTING })
        let myPlayerName = myPlayerId ?? gs.myPlayerId ?? sessionStorage.getItem("playerId")
        const mergedSeats = mergeServerWithSeats(gs.seats, data, myPlayerName)
        const newSeats = rotateSeats(mergedSeats)
        const dealerIndex = newSeats.findIndex(ele => ele.dealer)
        const handIndex = dealerIndex !== (newSeats.length - 1) ? (dealerIndex % newSeats.length) + 1 : 0
        const dealerSeatIndex = newSeats.find(ele => ele.dealer).serverSeatNumber

        dispatch({ type: "SET_SEATS", payload: [...newSeats] })
        dispatch({ type: "SET_DEALER", payload: dealerIndex })
        dispatch({ type: "SET_HAND_INDEX", payload: handIndex })
        dispatch({ type: "SET_DEALER_SEAT", payload: dealerSeatIndex })

        const firstTurn =
            dealerSeatIndex === newSeats.length ? 1 : dealerSeatIndex + 1

        dispatch({ type: "SET_TURN", payload: firstTurn })
        done?.()
    }, [])

    const sendPlayerAction = useCallback((type, card) => {
        const seats = seatsRef.current
        const gameState = gameStateRef.current
        const table = tableRef.current

        const seat =
            card?.seatNumber ??
            seats.find(player => player.playerId === gameState.myPlayerId)?.serverSeatNumber

        if (!validateAction(gameState, { type, seat })) {
            console.log("Invalid action rejected", type)
            return
        }

        props.sendOf({
            type,
            payload: {
                tableId: table.tableId,
                roundId: table.roundId,
                matchId: table.matchId,
                ...card
            }
        })

        dispatch({ type: "SET_PLAYER_ACTIONS", payload: { actions: [] } })
        dispatch({ type: "STOP_TIMER" })

        if (card) {
            dispatch({ type: "BLOCK_CARD_PLAY" })
            setPlayerActionMessage(`You played a ${card.cards.value}${card.cards.suit}. Waiting for your opponent's move.`)
            dispatch({ type: "NEXT_TURN" })
        }

        const betDispatches = {
            TRUCO: () => dispatch({ type: "CALL_TRUCO", payload: { seat: card?.seatNumber } }),
            RE_TRUCO: () => dispatch({ type: "RAISE_TRUCO", payload: { seat: card?.seatNumber } }),
            ACCEPT: () => dispatch({ type: "ACCEPT_BET" }),
            REJECT: () => dispatch({ type: "REJECT_BET" }),
            PASS: () => dispatch({ type: "PASS" }),
            ENVIDO: () => dispatch({ type: "CALL_ENVIDO", payload: { seat: card?.seatNumber } }),
            REAL_ENVIDO: () => dispatch({ type: "RAISE_ENVIDO", payload: { seat: card?.seatNumber } }),
            FALTA_ENVIDO: () => dispatch({ type: "RAISE_ENVIDO", payload: { seat: card?.seatNumber } }),
            ACCEPT_ENVIDO: () => dispatch({ type: "ACCEPT_BET" }),
            REJECT_ENVIDO: () => dispatch({ type: "REJECT_BET" }),
        }
        if (betDispatches[type]) {
            setPlayerActionMessage(buildMsg(myPlayerId, type, true))
            betDispatches[type]?.()
        }
    }, [props.sendOf])

    const leaveTableHandler = useCallback(() => {
        props.sendOf({ type: "LEAVE_TABLE", payload: { tableId: props.table?.tableId } })
    }, [props.sendOf, props.table?.tableId])

    const playAgain = useCallback(() => {
        props.sendOf({ type: "PLAY_AGAIN", payload: { tableId: props.table?.tableId } })
    }, [props.sendOf, props.table?.tableId])

    const muteTheme = useCallback(() => {
        setIsThemeMute(prev => !prev)
    }, [])

    // ── Settings handlers ──────────────────────────────────────────────────
    const openSettings = useCallback(() => dispatch({ type: "SET_SETTINGS_OPEN", payload: true }), [])
    const closeSettings = useCallback(() => dispatch({ type: "SET_SETTINGS_OPEN", payload: false }), [])
    const handleNotifPositionChange = useCallback((key) => {
        dispatch({ type: "SET_NOTIF_POSITION", payload: key })
    }, [])
    const handleThemeChange = useCallback((key) => {
        dispatch({ type: "SET_ACTIVE_THEME", payload: key })
        applyTheme(key)
    }, [])

    const dealCards = useCallback(() => {
        props.sendOf({ type: "START_DEAL" })
    }, [props.sendOf])

    /**
     * buildMsg - the single source of truth for every player-action message.
     *
     *   actorId    - playerId who performed the action
     *   actionType - raw server key: TRUCO, RE_TRUCO, ACCEPT, REJECT_ENVIDO …
     *   itIsMyTurn - true  → end with "It's your turn."
     *                false → end with "Waiting for your opponent."
     *
     * Returns a human-readable string suitable for display to the local player.
     */
    const buildMsg = useCallback((actorId, actionType, itIsMyTurn) => {
        const gs = gameStateRef.current
        const isMe = actorId === myPlayerId
        // const turn = itIsMyTurn
        const turn = gs.action.serverCanDraw
            ? "It's your turn - play a card or make a bet."
            : "Waiting for your opponent to play."

        switch (actionType) {
            // ── Truco betting ─────────────────────────────────────────────
            case "TRUCO":
                return isMe
                    ? "You called Truco! Waiting for your opponent to respond."
                    : `${actorId} called Truco! Accept, raise to Re-Truco, or decline.`

            case "RE_TRUCO":
                return isMe
                    ? "You raised to Re-Truco! Waiting for your opponent."
                    : `${actorId} raised the bet to Re-Truco! Accept, raise to Cuatro, or decline.`

            case "FOUR":
                return isMe
                    ? "You raised to Cuatro! Waiting for your opponent."
                    : `${actorId} raised the bet to Cuatro! Accept or decline.`

            // ── Envido betting ────────────────────────────────────────────
            case "ENVIDO":
                return isMe
                    ? "You called Envido! Waiting for your opponent."
                    : `${actorId} called Envido! Accept, raise, or decline.`

            case "REAL_ENVIDO":
                return isMe
                    ? "You raised to Real Envido! Waiting for your opponent."
                    : `${actorId} raised to Real Envido! Accept, raise to Falta Envido, or decline.`

            case "FALTA_ENVIDO":
                return isMe
                    ? "You called Falta Envido! Waiting for your opponent."
                    : `${actorId} called Falta Envido! Accept or decline.`

            // ── Accept ────────────────────────────────────────────────────
            // The acceptor always leads card play (true whether the original
            // call or a re-raise was accepted).  itIsMyTurn is set by the
            // caller as !opponentActed (I accepted → my turn).
            case "ACCEPT":
                return isMe
                    ? `You accepted the bet. ${turn}`
                    : `${actorId} accepted the bet. ${turn}`

            case "ACCEPT_ENVIDO":
                return isMe
                    ? "You accepted Envido. Waiting for the result."
                    : `${actorId} accepted Envido. ${turn}`

            // ── Reject ────────────────────────────────────────────────────
            // Bet is cancelled; the player who called the bet now leads,
            // so the caller inverts itIsMyTurn when passing it in.
            case "REJECT":
                return isMe
                    ? `You declined the bet. ${turn}`
                    : `${actorId} declined the bet. ${turn}`

            case "REJECT_ENVIDO":
                return isMe
                    ? `You declined Envido. ${turn}`
                    : `${actorId} declined Envido. ${turn}`

            // ── Pass ──────────────────────────────────────────────────────
            case "PASS":
                return isMe
                    ? `You passed. ${turn}`
                    : `${actorId} passed. ${turn}`

            // ── Leave ─────────────────────────────────────────────────────
            case "LEAVE_TABLE":
                return `${actorId} left the table.`

            default:
                return turn
        }
    }, [])

    // Kept so callers outside handlePlayerActions still compile
    const setPlayerMessage = useCallback((playerId, action, message = "", done = () => { }) => {
        if (message && !playerId && !action) {
            setPlayerActionMessage(message)
        } else {
            // legacy callers pass "Now It's Your Turn !!!" when it is the local player's turn
            const itIsMyTurn = message === "Now It's Your Turn !!!"
            setPlayerActionMessage(buildMsg(playerId, action, itIsMyTurn))
        }
        done()
    }, [buildMsg])

    const handlePlayerActions = useCallback((data, done) => {
        const gs = gameStateRef.current
        if (!validateAction(gs, { type: data.action })) {
            console.warn("Invalid action rejected", data)
            done?.(); return
        }

        // opponentActed = true  when the server event was caused by the opponent
        // (so the local player may now need to respond or lead).
        const opponentActed = data.playerId !== gs.myPlayerId

        if (!opponentActed) {
            dispatch({ type: "SET_PLAYER_ACTIONS", payload: { actions: [], drawCard: false } })
        }

        const handlers = {
            // ── Truco bets ─────────────────────────────────────────────────
            // After a call the OTHER player must respond, so:
            //   I called        → opponent must respond  (itIsMyTurn = false)
            //   Opponent called → I must respond         (itIsMyTurn = true)
            TRUCO: (data, done) => {
                dispatch({ type: "START_BETTING", payload: data.action })
                dispatch({ type: "CALL_TRUCO", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "TRUCO", opponentActed))
                done()
            },
            RE_TRUCO: (data, done) => {
                dispatch({ type: "RAISE_TRUCO", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "RE_TRUCO", opponentActed))
                done()
            },
            FOUR: (data, done) => {
                dispatch({ type: "RAISE_TRUCO", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "FOUR", opponentActed))
                done()
            },
            // ── Accept ─────────────────────────────────────────────────────
            // In Truco the player who ACCEPTS a bet (whether original or raised)
            // is the one who leads card play next.
            //
            //   A calls TRUCO  → B accepts       → B (acceptor) leads
            //   A calls TRUCO  → B raises FOUR   → A accepts → A (acceptor) leads
            //
            // So: itIsMyTurn = I am the acceptor = !opponentActed
            //   I accepted        → I lead    (itIsMyTurn = !opponentActed = true)
            //   Opponent accepted → they lead (itIsMyTurn = !opponentActed = false)
            ACCEPT: (data, done) => {
                dispatch({ type: "ACCEPT_BET", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "ACCEPT", !opponentActed))
                done()
            },
            ACCEPT_ENVIDO: (data, done) => {
                dispatch({ type: "ACCEPT_BET", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "ACCEPT_ENVIDO", !opponentActed))
                done()
            },
            // ── Reject / Pass ──────────────────────────────────────────────
            // Bet cancelled; whoever CALLED it (the non-rejecting player) leads.
            //   Opponent rejected my bet  → I called, so I lead    (itIsMyTurn = !opponentActed = true)
            //   I rejected opponent's bet → they called, they lead (itIsMyTurn = !opponentActed = false)
            REJECT: (data, done) => {
                dispatch({ type: "REJECT_BET", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "REJECT", !opponentActed))
                done()
            },
            REJECT_ENVIDO: (data, done) => {
                dispatch({ type: "REJECT_BET", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "REJECT_ENVIDO", !opponentActed))
                done()
            },
            PASS: (data, done) => {
                dispatch({ type: "PASS", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "PASS", !opponentActed))
                done()
            },
            LEAVE_TABLE: (data, done) => {
                setPlayerActionMessage(buildMsg(data.playerId, "LEAVE_TABLE", false))
                let newSeats = seats
                newSeats = newSeats.map(ele =>
                    ele.playerId === data.playerId
                        ? { ...ele, name: "", playerId: "" }
                        : ele
                );
                dispatch({ type: "SET_SEATS", payload: [...newSeats] })
                done()
            },
            // ── Envido bets ────────────────────────────────────────────────
            ENVIDO: (data, done) => {
                dispatch({ type: "CALL_ENVIDO", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "ENVIDO", opponentActed))
                done()
            },
            REAL_ENVIDO: (data, done) => {
                dispatch({ type: "RAISE_ENVIDO", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "REAL_ENVIDO", opponentActed))
                done()
            },
            FALTA_ENVIDO: (data, done) => {
                dispatch({ type: "RAISE_ENVIDO", payload: data.action })
                setPlayerActionMessage(buildMsg(data.playerId, "FALTA_ENVIDO", opponentActed))
                done()
            },
        }

        if (handlers[data.action]) {
            handlers[data.action](data, done)
        } else {
            done()
        }
    }, [setPlayerMessage, seats])

    const handlePlayerDrawCard = useCallback((data, done) => {
        const gs = gameStateRef.current
        const currentSeats = seatsRef.current

        if (gs.phase !== PHASE.PLAYING && gs.phase !== PHASE.TRICK_RESULT) {
            done?.()
            return
        }

        dispatch({ type: "STOP_TIMER" })

        const seatIndex = currentSeats.findIndex(
            ele => ele.serverSeatNumber === data.seatNumber
        )

        const player = currentSeats.find(
            ele => ele.serverSeatNumber === data.seatNumber
        )

        const requiredCard = data.cards.value + data.cards.suit

        const isMyCardToThrow = currentSeats.find(
            ele =>
                ele.serverSeatNumber === data.seatNumber &&
                ele.playerId === gs.myPlayerId
        )

        // ✅ Correct findIndex handling
        let index = player.cards.findIndex(c => c.cardValue === requiredCard)
        if (index === -1) index = 0

        const cardObj =
            player.cards.find(c => c.cardValue === requiredCard) ||
            player.cards[0]

        if (!cardObj) {
            done?.()
            return
        }

        const cardId = cardObj.id
        const card = document.getElementById(cardId)

        if (!card) {
            done?.()
            return
        }

        const {
            x,
            y,
            width: cardWidth,
            height: cardHeight,
            backgroundPosition: { backgroundPosition, spriteWidth, spriteHeight },
            widthGap,
            heightGap
        } = getCardPosition(seatIndex, requiredCard, !player?.isMe)

        let addtionalX = 0
        let addtionalY = 0

        if (index === 0) {
            addtionalX = -widthGap
        } else if (index === 2) {
            addtionalX = widthGap
            addtionalY = heightGap
        }

        const cardX = x + addtionalX
        const cardY = y + addtionalY

        // mark card as animating so layout effect ignores it
        card.dataset.animating = "true"

        card.style.bottom = 0
        card.style.left = 0
        card.style.backgroundPosition = backgroundPosition
        card.style.backgroundSize = `${spriteWidth}px ${spriteHeight}px`
        card.classList.remove("truco-card-back")

        let centerCardX, centerCardY, centerWidth, centerHeight

        if (gs.isCenterCardsStack) {
            const pos = getStackCenterCardPosition(
                seatIndex,
                requiredCard,
                gs.trickCount
            )
            centerCardX = pos.x
            centerCardY = pos.y
            centerWidth = pos.width
            centerHeight = pos.height
        } else {
            const pos = getCenterCardPosition(seatIndex, requiredCard)
            centerCardX = pos.x
            centerCardY = pos.y
            centerWidth = pos.width
            centerHeight = pos.height
        }

        const scaleX = centerWidth / cardWidth
        const scaleY = centerHeight / cardHeight
        let finalCenterX = centerCardX
        let finalCenterY = centerCardY
        // if (player.isMe) {
        //     if (scaleX - 1) finalCenterX += centerWidth * (scaleX - 1) / 2
        //     if (scaleY - 1) finalCenterY -= centerHeight * (scaleY - 1) / 2
        // } else {
        //     if (scaleX - 1) finalCenterX += centerWidth * (scaleX - 1) / 4
        //     if (scaleY - 1) finalCenterY -= centerHeight * (scaleY - 1) / 4
        // }

        card.style.transformOrigin = "bottom left"

        const animationCompleted = () => {
            delete card.dataset.animating
            // ✅ Remove card from hand BEFORE animation
            dispatch({
                type: "MOVE_CARD_TO_CENTER",
                payload: {
                    seatIndex,
                    cardId,
                    cardValue: requiredCard,
                    trickCount: gs.trickCount
                }
            })
            if (player.isMe) {
                dispatch({ type: "SET_PLAYER_ACTIONS", payload: { actions: [], drawCard: false } })
            }
            done?.()
        }

        const discardTl = discardTrucoCard({
            card,
            totalSeats: currentSeats.length,
            seatIndex,
            cardX,
            cardY,
            animationCompleted,
            dispatch,
            cardValue: requiredCard,
            centerCardX: finalCenterX,
            centerCardY: finalCenterY,
            scaleX,
            scaleY
        })
        if (discardTl) activeTimelinesRef.current.push(discardTl)

        dispatch({ type: "NEXT_TURN" })

        // Determine whose turn it is AFTER this card was played.
        // serverCanDraw is updated synchronously by NEXT_TURN in the reducer.
        const isMyCard = player.playerId === gs.myPlayerId
        const canGoNext = gameStateRef.current.action.serverCanDraw
        if (isMyCard) {
            // This fires for the local player's own throw (opponent sees it)
            setPlayerActionMessage(`You played a ${requiredCard}. Waiting for your opponent's move.`)
        } else if (canGoNext) {
            setPlayerActionMessage(`${player.playerId} played a ${requiredCard}. It's your turn - play a card or make a bet.`)
        } else {
            setPlayerActionMessage(`${player.playerId} played a ${requiredCard}. Waiting for your opponent's move.`)
        }
    }, [getCardPosition, getCenterCardPosition, getStackCenterCardPosition])

    const handleTrickWinner = useCallback((data, done) => {
        const gs = gameStateRef.current
        if (gs.phase !== PHASE.PLAYING) { done?.(); return }
        dispatch({ type: "STOP_TIMER" })

        dispatch({ type: "SET_PHASE", payload: PHASE.TRICK_RESULT })

        const myData = data.find(player => player.playerId === gs.myPlayerId)
        const winner = data.find(player => player.winner)
        const amIWinner = myData.winner
        const MAX_TRICKS = 3

        const winIndicator = document.querySelector(`#handWinIndicator-${gs.trickCount}`)
        if (winIndicator) {
            winIndicator.classList.add(amIWinner ? "indicateWin" : "indicateLose")
        }

        // ── Winner-card-on-top animation ──────────────────────────────────
        // Find the display-seat index of the winner so animateTrickResult can
        // identify which center-card DOM element belongs to the winner.
        const winnerSeatIndex = seatsRef.current.findIndex(
            s => s.serverSeatNumber === winner.seatNumber
        )
        // Small delay so both cards are guaranteed to have landed at their
        // center positions before we start the winner animation.
        setTimeout(() => {
            animateTrickResult({
                trickCount: gs.trickCount,
                winnerSeatIndex,
                totalSeats: seatsRef.current.length
            })
        }, 150)

        dispatch({ type: "SET_TRICK", payload: (gs.trickCount % MAX_TRICKS) + 1 })
        dispatch({ type: "SET_TURN", payload: winner.seatNumber })
        dispatch({ type: "SET_PLAYER_ACTIONS", payload: { actions: [], drawCard: false } })
        // Record the trick outcome on every seat's trickWins array
        dispatch({
            type: "UPDATE_TRICK_WIN",
            payload: { winnerPlayerId: winner.playerId, trickIndex: gs.trickCount - 1 }
        })

        if (amIWinner) {
            setPlayerActionMessage("You won the trick! You lead the next card.")
        } else {
            setPlayerActionMessage(`${winner.playerId} won the trick. Waiting for their lead.`)
        }
        done?.()
    }, [])

    const handleTrickDraw = useCallback((data, done) => {
        const gs = gameStateRef.current
        if (gs.phase !== PHASE.PLAYING) { done?.(); return }

        dispatch({ type: "STOP_TIMER" })
        dispatch({ type: "SET_PHASE", payload: PHASE.TRICK_RESULT })

        const MAX_TRICKS = 3
        const winIndicator = document.querySelector(`#handWinIndicator-${gs.trickCount}`)
        if (winIndicator) winIndicator.classList.add("indicateDraw")

        // ── Draw: dim all cards for this trick equally ────────────────────
        setTimeout(() => {
            const allCenterCards = Array.from(
                document.querySelectorAll(`[id^="truco-center-card-"]`)
            )
            allCenterCards
                .filter(el => {
                    const parts = el.id.split("-")
                    return parseInt(parts[4], 10) === gs.trickCount
                })
                .forEach(el => {
                    gsap.to(el, { filter: "brightness(0.7)", duration: 0.3 })
                })
        }, 150)

        dispatch({ type: "SET_TRICK", payload: (gs.trickCount % MAX_TRICKS) + 1 })
        // Record draw outcome on every seat's trickWins array
        dispatch({
            type: "UPDATE_TRICK_WIN",
            payload: { winnerPlayerId: null, trickIndex: gs.trickCount - 1 }
        })

        const { dealerSeat } = gs.turn
        const firstTurn = dealerSeat === seatsRef.current.length ? 1 : dealerSeat + 1

        dispatch({ type: "SET_TURN", payload: firstTurn })
        dispatch({ type: "SET_PLAYER_ACTIONS", payload: { actions: [], drawCard: false } })

        // firstTurn is the server seat number who leads after this draw.
        // Compare it against the local player's serverSeatNumber to find out
        // if it is actually their turn - NOT gs.dealerIndex which is a
        // display-array index and has nothing to do with whose turn it is.
        const myServerSeat = seatsRef.current.find(s => s.isMe)?.serverSeatNumber
        const iAmNext = firstTurn === myServerSeat
        if (iAmNext) {
            setPlayerActionMessage("Trick draw - no winner. You lead the next trick.")
        } else {
            setPlayerActionMessage("Trick draw - no winner. Your opponent leads the next trick.")
        }
        done?.()
    }, [])

    const handleMatchWinner = useCallback((data, done) => {
        const gs = gameStateRef.current
        if (gs.phase !== PHASE.PLAYING && gs.phase !== PHASE.TRICK_RESULT) {
            done?.(); return
        }

        dispatch({ type: "STOP_TIMER" })
        dispatch({ type: "SET_PHASE", payload: PHASE.MATCH_RESULT })
        dispatch({ type: "SET_PLAYER_ACTIONS", payload: { actions: [], drawCard: false } })
        if (!data.length) return

        dispatch({ type: "SET_TRICK", payload: 1 })

        const myData = data.find(player => player.playerId === gs.myPlayerId)
        const winner = data.find(player => player.winner)
        const amIWinner = myData.winner

        if (amIWinner) {
            setPlayerActionMessage("Congratulations! You won the hand.")
        } else {
            setPlayerActionMessage(`${winner.playerId} won the hand.`)
        }

        const opponentPoints = data
            .filter(player => player.playerId !== gs.myPlayerId)
            .reduce((total, player) => total + player.points, 0)
        const myPoints = data.find(p => p.playerId === gs.myPlayerId)?.points ?? 0

        dispatch({ type: "UPDATE_POINTS", payload: { myPoints, opponentPoints } })

        setSafeTimeout(() => {
            document.querySelectorAll(".handWinIndicator")
                .forEach(ele => ele.classList.remove("indicateWin", "indicateLose", "indicateDraw"))
            const newSeats = seatsRef.current.map(ele => ({ ...ele, cards: [] }))
            dispatch({ type: "SET_SEATS", payload: newSeats })
            dispatch({ type: "RESET_HAND_STATE" })
            dispatch({ type: "NEXT_HAND" })
            done()
        }, 5000)
    }, [setSafeTimeout])

    const handleGameWinner = useCallback((data, done) => {
        const gs = gameStateRef.current
        dispatch({ type: "SET_PHASE", payload: PHASE.GAME_RESULT })
        dispatch({ type: "STOP_TIMER" })

        const amIWinner = data.playerId === gs.myPlayerId
        let { myPoints, opponentPoints } = gs.points
        if (amIWinner) myPoints = data.points
        else opponentPoints = data.points

        const winIndicator = document.querySelector(`#handWinIndicator-${gs.trickCount}`)
        if (winIndicator) {
            winIndicator.classList.add(amIWinner ? "indicateWin" : "indicateLose")
        }

        dispatch({ type: "UPDATE_POINTS", payload: { myPoints, opponentPoints } })
        let msg = amIWinner ? "Congratulations! You won the game!" : `${data.playerId} won the game.`
        setPlayerActionMessage(msg)

        dispatch({ type: "SET_GAME_WIN_DETAILS", payload: { ...data, amIWinner, message: msg } })
        dispatch({ type: "GAME_COMPLETED" })
        done()
    }, [])

    const handleEnvidoResult = useCallback((data, done) => {
        const gs = gameStateRef.current

        if (gs.phase !== PHASE.PLAYING) { done?.(); return }

        dispatch({ type: "STOP_TIMER" })
        dispatch({ type: "SET_PHASE", payload: PHASE.PLAYING })
        if (data?.reason === "DONT_ACCEPT") {
            let { myPoints, opponentPoints } = points
            if (data?.envidoWinner === myPlayerId) {
                myPoints = data.userPoints
                setPlayerActionMessage(`${data.envidoReject} Rejected The Envido. You Got ${data.pointsAwarded} points.`)
            } else if (data?.envidoReject === myPlayerId) {
                opponentPoints = data.userPoints
                setPlayerActionMessage(`You Rejected The Envido. ${data.envidoWinner} Got ${data.pointsAwarded} points.`)
            } else {
                opponentPoints = data.userPoints
                setPlayerActionMessage(`${data?.envidoReject} Rejected The Envido. ${data.envidoWinner} Got ${data.pointsAwarded} points.`)
            }
            dispatch({ type: "UPDATE_POINTS", payload: { myPoints, opponentPoints } })

            done?.()
            return
        }
        const winner = data.find(ele => ele.winner)
        if (!winner) {
            setPlayerActionMessage("Envido is a draw! Check the points.")
            done(); return
        }
        const amIWinner = winner.playerId === gs.myPlayerId

        setPlayerActionMessage(
            amIWinner
                ? "You won the Envido!"
                : `${winner.playerId} won the Envido.`
        )

        const { points: opponentPoints, envidoPoints: opponentEnvidoPoints } = data
            .find(player => player.playerId !== gs.myPlayerId)
        // .filter(player => player.playerId !== gs.myPlayerId)
        // .reduce((total, player) => total + player.envidoPoints, 0)
        const { points: myPoints, envidoPoints: playerEnvidoPoints } = data.find(p => p.playerId === gs.myPlayerId) ?? 0
        let envidoDetails = {
            winnerName: winner.playerId,
            winnerPoints: winner.envidoPoints,
            playerEnvidoPoints,
            opponentEnvidoPoints,
        }
        dispatch({ type: "UPDATE_POINTS", payload: { myPoints, opponentPoints } })
        dispatch({ type: "SET_ENVIDO_DETAILS", payload: envidoDetails })
        setSafeTimeout(() => {
            done()
        }, 3000)
    }, [setSafeTimeout])

    const clearActionButtons = useCallback(() => {
        dispatch({ type: "SET_PLAYER_ACTIONS", payload: { actions: [] } })
    }, [])

    // ── Derived memoized values ────────────────────────────────────────────
    const users = useMemo(() => {
        if (playerDetails.length === 2) {
            return {
                you: myPlayerId ?? "You",
                opponent: playerDetails.find(ele => ele.playerId !== myPlayerId)?.playerId ?? "Opponent"
            }
        }
        return { you: "You", opponent: "Opponent" }
    }, [playerDetails, myPlayerId])

    const isMyTurn = action.serverCanDraw && action.canPlayCard && !isCardsDealing

    const getCenterCardPos = isCenterCardsStack ? getStackCenterCardPosition : getCenterCardPosition

    const closeEnvidoDetails = useCallback(() => {
        dispatch({ type: "SET_ENVIDO_DETAILS", payload: null })
    }, [])

    const closeGameWinDetails = useCallback(() => {
        dispatch({ type: "SET_GAME_WIN_DETAILS", payload: null })
    }, [])

    const quitGameHandler = useCallback((type) => {
        // ── Kill any in-flight GSAP animations before resetting ───────────
        // Stale deal/discard timelines hold closures over the old seat refs.
        // If they fire after RESET_GAME they dispatch ADD_CARD_TO_SEAT into
        // the freshly-wiped reducer, dirtying the new hand's state.
        activeTimelinesRef.current.forEach(tl => tl.kill?.())
        activeTimelinesRef.current = []
        // ── Reset React-local state that the reducer cannot touch ─────────
        // isCardsDealing lives outside the reducer. If it is already false
        // when the next deal runs, setIsCardsDealing(true -> false) produces
        // no change notification and CardHand never re-renders with new cards.
        // Force it back to true so the upcoming deal starts clean.
        setIsCardsDealing(true)
        setPlayerActionMessage("")

        dispatch({ type: "RESET_GAME" })
        closeGameWinDetails()
        const { serverSeatNumber: seatNumber } = seats.find(ele => ele.playerId === myPlayerId)
        props.sendOf({ type, payload: { tableId: table.tableId, playerId: myPlayerId, seatNumber } })
    }, [table, seats, closeGameWinDetails])
    // ── useImperativeHandle ────────────────────────────────────────────────
    // Deps use refs where possible to avoid re-registering on every state change.
    // Only `trickCount`, `isCardsDealing` are real deps because they are read
    // directly (not via ref) inside these handlers.
    useImperativeHandle(ref, () => ({
        sendPlayerAction,
        handleDealTrucoCards,
        setMatchStart,
        setPlayerDetails: (data, done) => {
            dispatch({ type: "SET_PLAYER_DETAILS", payload: data })
            done()
        },
        setPlayerActionButtons: (data, done) => {
            dispatch({ type: "SET_PLAYER_ACTIONS", payload: data })
            if (data.drawCard) {
                dispatch({ type: "START_CARD_PLAY" })
                // This is the canonical source of "whose turn is it" after any
                // server event (deal, trick result, bet resolution, etc.).
                // drawCard=true means the server has given this player the right
                // to act; drawCard=false means they must wait.
                if (data.actions.length >= 2) {
                    setPlayerActionMessage("It's your turn - play a card or make a bet.")
                }
            } else {
                setPlayerActionMessage("Waiting for your opponent to play.")
            }
            done()
        },
        handlePlayerActions,
        handlePlayerDrawCard,
        handleTrickDraw,
        handleTrickWinner,
        handleMatchWinner,
        handleGameWinner,
        handleEnvidoResult,
        checkGameResult: (data, done) => {
            const currentSeats = seatsRef.current
            let newSeats = currentSeats.map(ele => ({
                ...ele,
                cards: [],
                points: data.find(point => point.seatNumber === ele.serverSeatNumber).points
            }))
            dispatch({ type: "SET_SEATS", payload: [...newSeats] })
            done()
        },
        handleTimerStart: (data, done) => {
            const currentSeats = seatsRef.current
            dispatch({
                type: "START_TIMER",
                payload: {
                    ...data,
                    timerDuration: data.time,
                    seatId: currentSeats.findIndex(ele => ele.serverSeatNumber === data.seatId)
                }
            })
            done()
        },
        handleRestartRequest: (data, done) => {
            dispatch({ type: "SET_SHOW_GAME_RESTART" })
            done?.()
        },
        handleStopGameTimer: () => {
            dispatch({ type: "STOP_TIMER" })
        },
        // Toggle symmetric mirroring of opponent seats at runtime
        setSymmetricOpponents: (value) => {
            dispatch({ type: "SET_SYMMETRIC_OPPONENTS", payload: value })
        }
        // Only stable callbacks/dispatch here - no gameState spread
    }), [sendPlayerAction, handleDealTrucoCards, setMatchStart, handlePlayerActions,
        handlePlayerDrawCard, handleTrickDraw, handleTrickWinner, handleMatchWinner,
        handleGameWinner])


    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="table-canvas-container">
            <div ref={containerRef} className="truco-table-container">
                {/* {Array.from({ length: 6 }).fill(0).map((_, index) => {
                    return ( */}
                <>
                    {timer.seatNumber !== null && timer.seatNumber !== undefined && (
                        <TrucoTimer
                            {...getTimerPosition(timer.seatNumber)}
                            {...timer}
                        />
                    )}
                    {timer.seatNumber !== null && timer.seatNumber !== undefined && (
                        <TrucoDigitalTimer
                            {...getDigitalTimerPosition(timer.seatNumber)}
                            {...timer}
                        />
                    )}

                </>
                {/* )
                })} */}
                {showEnvidoDetails && (
                    <EnvidoPopUpMessage envidoDetails={envidoDetails} onClose={closeEnvidoDetails} />
                )}
                {showGameWinDetails && (
                    <TrucoGameWinPopUp gameWinnerDetails={gameWinnerDetails} quitGameHandler={quitGameHandler} showRestartGameOptions={showRestartGameOptions} onClose={closeGameWinDetails} playAgain={playAgain} leaveTableHandler={leaveTableHandler} />
                )}

                {/* ── Game toast notifications (replaces TrucoInfoMessage) ── */}
                <GameToastNotification message={playerActionMessage}
                    positionKey={notifPosition}
                />
                {/* ── Settings dialog ── */}
                <SettingsDialog
                    isOpen={isSettingsOpen}
                    onClose={closeSettings}
                    notifPosition={notifPosition}
                    onPositionChange={handleNotifPositionChange}
                    activeTheme={activeTheme}
                    onThemeChange={handleThemeChange}
                />
                <TableSurface isPortrait={isPortrait} />
                <TableAndMatchInfo tableId={tableRef.current?.tableId} matchId={tableRef.current?.matchId} roundId={tableRef.current?.roundId}></TableAndMatchInfo>
                <CenterCards
                    totalSeats={seats.length}
                    isCenterCardsStack={isCenterCardsStack}
                    getStackCenterCardPosition={getStackCenterCardPosition}
                    getCenterCardPosition={getCenterCardPosition}
                    centerCards={centerCards}
                />
                <CheckAndCrossMarksForCenterCards seats={seats} getCenterCardPos={getCenterCardPos}></CheckAndCrossMarksForCenterCards>
                {Boolean(playerDetails.length && dealerIndex !== null) && (
                    <PointsAndScoring
                        seats={seats}
                        turn={turn}
                        bet={bet}
                        myPlayerId={myPlayerId}
                        points={points}
                        maxPoints={15}
                        handIndex={handIndex}
                        dealerIndex={dealerIndex}
                    />
                )}
                <SeatsLayer
                    seats={seats}
                    seatRefs={seatRefs}
                    isMyTurn={isMyTurn}
                    dispatch={dispatch}
                    isCardsDealing={isCardsDealing}
                    getSeatPosition={getSeatPosition}
                    getCardPosition={getCardPosition}
                    getCenterCardPos={getCenterCardPos}
                    clearActionButtons={clearActionButtons}
                    sendPlayerAction={sendPlayerAction}
                    symmetricOpponents={symmetricOpponents}
                    {...timer}
                />
                {Boolean(playerActions.length) && (
                    <PlayerActions playerActions={playerActions} sendPlayerAction={sendPlayerAction} />
                )}
                {dealerIndex !== null && (
                    <DealerIcon
                        ref={dealerRef}
                        getDealerIconPosition={getDealerIconPosition}
                        currentDealer={dealerIndex}
                    />
                )}
                <TableHeader
                    leaveTableHandler={leaveTableHandler}
                    isMute={isThemeMuted}
                    muteTheme={muteTheme}
                    dealCards={dealCards}
                    table={tableRef.current}
                    onSettingsOpen={openSettings}
                />
            </div>
            {/* <Stage height={isPortrait ? 2350 : 1250} width={isPortrait ? 1250 : 2350}>
                <Layer>
                    <AlignGrid height={isPortrait ? 2350 : 1250} width={isPortrait ? 1250 : 2350} rows={20} cols={isPortrait ? 10 : 20}></AlignGrid>
                </Layer>
            </Stage> */}
        </div >
    )
}))